import React, { useEffect, useRef } from 'react';

const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

let leafletPromise = null;

const ensureLeaflet = () => {
  if (typeof window !== 'undefined' && window.L) {
    return Promise.resolve(window.L);
  }
  if (leafletPromise) {
    return leafletPromise;
  }

  leafletPromise = new Promise((resolve) => {
    if (typeof document === 'undefined') {
      resolve(null);
      return;
    }

    if (!document.querySelector('link[data-leaflet="css"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      link.dataset.leaflet = 'css';
      document.head.appendChild(link);
    }

    const existingScript = document.querySelector('script[data-leaflet="js"]');
    if (existingScript && window.L) {
      resolve(window.L);
      return;
    }

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.L));
      return;
    }

    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.async = true;
    script.dataset.leaflet = 'js';
    script.onload = () => resolve(window.L);
    document.body.appendChild(script);
  });

  return leafletPromise;
};

export default function MapPreview({ lat, lng, zoom = 15, height = 240 }) {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const initialise = async () => {
      if (lat == null || lng == null) return;
      const L = await ensureLeaflet();
      if (!L || cancelled) return;
      if (!mapContainerRef.current) return;

      if (!mapInstanceRef.current) {
        mapInstanceRef.current = L.map(mapContainerRef.current, {
          zoomControl: true,
          attributionControl: true,
        });
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; OpenStreetMap contributors',
        }).addTo(mapInstanceRef.current);
      }

      mapInstanceRef.current.setView([lat, lng], zoom);

      if (markerRef.current) {
        markerRef.current.setLatLng([lat, lng]);
      } else {
        markerRef.current = L.marker([lat, lng]).addTo(mapInstanceRef.current);
      }
    };

    initialise();

    return () => {
      cancelled = true;
    };
  }, [lat, lng, zoom]);

  useEffect(() => () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
      markerRef.current = null;
    }
  }, []);

  if (lat == null || lng == null) {
    return null;
  }

  return (
    <div
      ref={mapContainerRef}
      style={{ height, width: '100%', borderRadius: '0.5rem', overflow: 'hidden' }}
    />
  );
}
